import type { ReactNode } from 'react';
import { useInView } from '../../lib/motion';

interface Step {
  no: string;
  title: string;
  body: ReactNode;
  art: ReactNode;
  tag: string;
}

const STEPS: Step[] = [
  {
    no: '01',
    title: 'We review your profile',
    body: 'Our team reviews every Early Access application before activating an account.',
    art: <ReviewArt />,
    tag: 'Now'
  },
  {
    no: '02',
    title: 'Your invitation arrives by email',
    body: (
      <>
        If your profile is selected for the current access wave, we&rsquo;ll send your personal Frameon
        invitation directly to your email. Keep an eye on your inbox.
      </>
    ),
    art: <InviteArt />,
    tag: 'Next'
  },
  {
    no: '03',
    title: 'You may receive free creation credits',
    body: (
      <>
        Creators invited during Early Access can receive complimentary credits to explore Frameon. The amount
        may vary with your profile, your experience with AI production and how complete your profile is.
      </>
    ),
    art: <CreditsArt />,
    tag: 'Then'
  }
];

/** Three stations on one line, with a light running from where you are to where you are going. */
export default function NextSteps() {
  const [ref, seen] = useInView<HTMLOListElement>(0.2);

  return (
    <ol ref={ref} className={`steps${seen ? ' in' : ''}`}>
      <span className="steps-rail" aria-hidden="true">
        <i />
      </span>
      {STEPS.map((step, index) => (
        <li key={step.no} className={`stepc${index === 0 ? ' now' : ''}`} style={{ '--d': `${index * 160}ms` } as React.CSSProperties}>
          <div className="stepc-node" aria-hidden="true">
            <span />
          </div>
          <div className="stepc-art" aria-hidden="true">
            {step.art}
          </div>
          <div className="stepc-meta">
            <span className="mono">{step.no}</span>
            <span className="stepc-tag">{step.tag}</span>
          </div>
          <h3>{step.title}</h3>
          <p>{step.body}</p>
        </li>
      ))}
    </ol>
  );
}

/** A profile card with a lens passing over it. */
function ReviewArt() {
  return (
    <svg viewBox="0 0 160 110">
      <rect x="28" y="14" width="104" height="82" rx="3" className="a-line" />
      <circle cx="52" cy="40" r="11" className="a-line" />
      <rect x="70" y="32" width="46" height="5" className="a-fill-dim" />
      <rect x="70" y="42" width="30" height="5" className="a-fill-dim" />
      <rect x="40" y="62" width="80" height="4" className="a-fill-dim" />
      <rect x="40" y="71" width="64" height="4" className="a-fill-dim" />
      <rect x="40" y="80" width="72" height="4" className="a-fill-dim" />
      <g className="a-lens">
        <circle cx="0" cy="0" r="17" className="a-lens-glass" />
        <circle cx="0" cy="0" r="17" className="a-mag-line" />
        <line x1="12" y1="12" x2="25" y2="25" className="a-mag-line thick" />
      </g>
    </svg>
  );
}

/** An envelope with the invitation lifting out of it. */
function InviteArt() {
  return (
    <svg viewBox="0 0 160 110">
      <g className="a-letter">
        <rect x="46" y="22" width="68" height="56" rx="2" className="a-paper" />
        <rect x="56" y="33" width="40" height="4" className="a-fill-mag" />
        <rect x="56" y="43" width="48" height="3" className="a-fill-dim" />
        <rect x="56" y="51" width="36" height="3" className="a-fill-dim" />
      </g>
      <path d="M34 50 L80 80 L126 50 L126 98 L34 98 Z" className="a-env" />
      <path d="M34 98 L72 70 M126 98 L88 70" className="a-line" />
      <path d="M34 50 L80 80 L126 50" className="a-line" />
      <circle cx="80" cy="80" r="6" className="a-seal" />
    </svg>
  );
}

/** A stack of credit tokens, with a spark above. */
function CreditsArt() {
  const coins = [0, 1, 2, 3];
  return (
    <svg viewBox="0 0 160 110">
      {coins.map(i => (
        <g key={i} className="a-coin" style={{ '--i': i } as React.CSSProperties}>
          <ellipse cx="80" cy={88 - i * 11} rx="30" ry="8" className="a-coin-face" />
          <path d={`M50 ${88 - i * 11} v6 a30 8 0 0 0 60 0 v-6`} className="a-line" />
        </g>
      ))}
      <text x="80" y="58" textAnchor="middle" className="a-coin-mark">
        CR
      </text>
      <g className="a-spark">
        <path d="M122 18 l3 8 8 3 -8 3 -3 8 -3 -8 -8 -3 8 -3z" className="a-fill-mag" />
        <path d="M36 30 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2z" className="a-fill-fg" />
      </g>
    </svg>
  );
}
